import CheckIcon from '@mui/icons-material/Check';
import { Box, Stack, Typography, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StatusChip } from 'features/ui';
import type { EnrollmentStep, EnrollmentStepId } from '../types';

const STEP_KEYS: Record<EnrollmentStepId, { name: string; blurb: string }> = {
  prescreening: {
    name: 'EnrollmentStepPrescreeningName',
    blurb: 'EnrollmentStepPrescreeningBlurb',
  },
  consent: {
    name: 'EnrollmentStepConsentName',
    blurb: 'EnrollmentStepConsentBlurb',
  },
};

const CIRCLE_SIZE = 36;

type EnrollmentPathProps = {
  steps: EnrollmentStep[];
};

/** Vertical enrollment path — numbered circles joined by a rail. */
export const EnrollmentPath = ({ steps }: EnrollmentPathProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ position: 'relative', py: 0.5 }}>
      <Box
        aria-hidden
        sx={theme => ({
          position: 'absolute',
          left: CIRCLE_SIZE / 2 - 1,
          top: 26,
          bottom: 26,
          width: 2,
          backgroundColor: theme.palette.divider,
        })}
      />
      <Stack
        component="ol"
        spacing={2.25}
        sx={{ listStyle: 'none', m: 0, p: 0 }}
      >
        {steps.map((step, index) => {
          const done = step.status === 'done';
          const current = step.status === 'current';
          const keys = STEP_KEYS[step.id];
          return (
            <Box
              key={step.id}
              component="li"
              sx={{
                position: 'relative',
                display: 'flex',
                gap: 1.75,
                alignItems: 'flex-start',
              }}
            >
              <Box
                aria-hidden
                sx={theme => ({
                  width: CIRCLE_SIZE,
                  height: CIRCLE_SIZE,
                  borderRadius: '50%',
                  flexShrink: 0,
                  position: 'relative',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: done
                    ? theme.ui.timelineDone
                    : theme.palette.background.paper,
                  border: done
                    ? 'none'
                    : `${current ? '3' : '2'}px solid ${
                        current
                          ? theme.palette.primary.main
                          : theme.ui.surfaceBorder
                      }`,
                  boxShadow: current
                    ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.14)}`
                    : 'none',
                })}
              >
                {done ? (
                  <CheckIcon
                    sx={theme => ({
                      fontSize: 18,
                      color: theme.palette.getContrastText(
                        theme.ui.timelineDone
                      ),
                    })}
                  />
                ) : (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 14,
                      fontWeight: 800,
                      color: current ? 'primary.main' : 'text.tertiary',
                    }}
                  >
                    {index + 1}
                  </Typography>
                )}
              </Box>
              <Box sx={{ pt: 0.25 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: done || current ? 'text.primary' : 'text.tertiary',
                    }}
                  >
                    {t(keys.name)}
                  </Typography>
                  {current && (
                    <StatusChip
                      label={t('EnrollmentStepNext')}
                      kind="primary"
                    />
                  )}
                  {done && (
                    <Typography
                      component="span"
                      sx={theme => ({
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: theme.ui.timelineDone,
                      })}
                    >
                      {t('EnrollmentStepDone')}
                    </Typography>
                  )}
                </Stack>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: 'text.secondary',
                    mt: 0.25,
                    maxWidth: 420,
                  }}
                >
                  {t(keys.blurb)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
